// What a new strategy content (a dropped graph.json, or one Cowork sends: functions/src/http/strategyAgent.ts) changes,
// shown before it is loaded (web/src/strategy/ContentCheck.tsx) or returned by the agent's check:
// the elements, links and sources it adds and removes, and the work people saved that it would leave behind.
// Everything saved is kept by id (domain/strategy.ts): an element a new build drops or renames leaves its edits,
// priority places, RACI rows, own links and subprojects pointing at nothing. And a Strategy Integrator run
// numbers the content's links in order (build.py: R001, R002…), so a link added or dropped in the middle
// moves every later number: the decisions on them would apply to other links. remapDecisions moves them back.
import { libraryEntries, sourcesOf } from './library.ts';
import { strategyChanges, type StrategyChange, type StrategyState } from './strategy.ts';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const list = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : []);
const s = (v: unknown): string => (v == null ? '' : String(v));

export interface Named {
  id: string;
  title: string;
}

/** Saved work that would lose its element: what it is, under which key, the ids it names that go. */
export interface Orphan {
  what: 'decision' | 'edit' | 'link' | 'order' | 'raci' | 'subproject' | 'library';
  key: string;
  ids: string[];
}

export interface ContentCheck {
  counts: { before: Counts; after: Counts };
  elements: { added: Named[]; removed: Named[] };
  /** Removed and added with the same title: most likely an id renamed. */
  renamed: { from: string; to: string; title: string }[];
  links: { added: number; removed: number };
  sources: { added: string[]; removed: string[] };
  orphans: Orphan[];
  /** Decisions whose link number now names another link: [number, the link it was, the link it would be]. */
  moved: { id: string; was: string; now: string }[];
  /** The same elements, links and sources: loading it changes nothing people decided. */
  same: boolean;
}

interface Counts {
  elements: number;
  links: number;
  sources: number;
}

/** Every element of a content, by id: its title (Lithuanian, as the content has it). */
function elementsOf(c: Obj): Map<string, string> {
  const out = new Map<string, string>();
  for (const k of ['directions', 'external', 'projects']) for (const x of list(c[k])) if (s(x.id)) out.set(s(x.id), s(x.title));
  return out;
}

const linkText = (r: Obj | undefined): string => (r ? `${s(r.from_ ?? r.from)} → ${s(r.to)} (${s(r.type)})` : '');
const relationsOf = (c: Obj): Map<string, Obj> => new Map(list(c.relations).map((r) => [s(r.id), r]));
const plainTitle = (t: string) => t.trim().toLowerCase().replace(/\s+/g, ' ');

/** The elements people added in the views: theirs, whatever the content holds. */
const ownElements = (state: StrategyState): Set<string> =>
  new Set(Object.entries(state.edits ?? {}).flatMap(([id, e]) => (isObj(e) && e.isNew ? [id] : [])));

/** What loading `after` in place of `before` changes. No content before: nothing saved can lose anything. */
export function checkContent(before: Obj | null, after: Obj, state: StrategyState): ContentCheck {
  const b = before ?? {};
  const was = elementsOf(b);
  const now = elementsOf(after);
  const added = [...now].filter(([id]) => !was.has(id)).map(([id, title]) => ({ id, title }));
  const removed = [...was].filter(([id]) => !now.has(id)).map(([id, title]) => ({ id, title }));
  // A renamed id: one removed and one added with the same title, each the only one with it.
  const byTitle = (xs: Named[]) => {
    const m = new Map<string, Named[]>();
    for (const x of xs) if (x.title.trim()) m.set(plainTitle(x.title), [...(m.get(plainTitle(x.title)) ?? []), x]);
    return m;
  };
  const addedBy = byTitle(added);
  const renamed = [...byTitle(removed)].flatMap(([t, xs]) => {
    const ys = addedBy.get(t);
    return xs.length === 1 && ys?.length === 1 ? [{ from: xs[0]!.id, to: ys[0]!.id, title: xs[0]!.title }] : [];
  });

  const relsWas = relationsOf(b);
  const relsNow = relationsOf(after);
  const pair = (r: Obj) => linkText(r);
  const pairsWas = new Set([...relsWas.values()].map(pair));
  const pairsNow = new Set([...relsNow.values()].map(pair));
  const srcWas = new Set(sourcesOf(b).map((x) => x.id));
  const srcNow = new Set(sourcesOf(after).map((x) => x.id));

  // What goes: in the old content, not in the new (people's own elements stay theirs).
  const own = ownElements(state);
  const lost = new Set(removed.map((x) => x.id).filter((id) => !own.has(id)));
  const gone = (ids: string[]) => ids.filter((id) => lost.has(id));
  const orphans: Orphan[] = [];
  const add = (what: Orphan['what'], key: string, ids: string[]) => {
    if (ids.length) orphans.push({ what, key, ids });
  };
  const customLinks = state.links ?? {};
  const moved: ContentCheck['moved'] = [];
  for (const id of Object.keys(state.decisions ?? {})) {
    if (id in customLinks || !relsWas.has(id)) continue;
    const r = relsNow.get(id);
    // Its number gone: lost only when its link is gone too; else remapDecisions carries it to the link's new number.
    if (!r) {
      if (!pairsNow.has(pair(relsWas.get(id)!))) add('decision', id, [linkText(relsWas.get(id))]);
    } else if (pair(r) !== pair(relsWas.get(id)!)) moved.push({ id, was: linkText(relsWas.get(id)), now: linkText(r) });
  }
  for (const id of Object.keys(state.edits ?? {})) add('edit', id, gone([id]));
  for (const [id, l] of Object.entries(customLinks)) if (isObj(l)) add('link', id, gone([s(l.from_ ?? l.from), s(l.to)]));
  for (const [inst, ids] of Object.entries(state.orders ?? {})) if (Array.isArray(ids)) add('order', inst, gone(ids.map(s)));
  for (const [inst, r] of Object.entries(state.raci ?? {})) if (isObj(r) && isObj(r.cells)) add('raci', inst, gone(Object.keys(r.cells)));
  for (const [key, p] of Object.entries(state.consolidation ?? {})) {
    if (!key.includes('|') || !isObj(p) || !Array.isArray(p.subs)) continue;
    add('subproject', key.replace('|', ' '), gone(list(p.subs).map((x) => s(x.ref)).filter(Boolean)));
  }
  // Documents whose source the new content no longer has: they would read "not read into the strategy yet".
  const { files, links } = libraryEntries(state);
  const docs = new Map<string, number>();
  for (const l of [...files.values(), ...links]) if (l.src && srcWas.has(l.src) && !srcNow.has(l.src)) docs.set(l.src, (docs.get(l.src) ?? 0) + 1);
  for (const [src, n] of docs) add('library', src, [`${n}`]);

  const sourcesAdded = [...srcNow].filter((x) => !srcWas.has(x));
  const sourcesRemoved = [...srcWas].filter((x) => !srcNow.has(x));
  const linksAdded = [...pairsNow].filter((p) => !pairsWas.has(p)).length;
  const linksRemoved = [...pairsWas].filter((p) => !pairsNow.has(p)).length;
  return {
    counts: {
      before: { elements: was.size, links: relsWas.size, sources: srcWas.size },
      after: { elements: now.size, links: relsNow.size, sources: srcNow.size },
    },
    elements: { added, removed },
    renamed,
    links: { added: linksAdded, removed: linksRemoved },
    sources: { added: sourcesAdded, removed: sourcesRemoved },
    orphans,
    moved,
    same: !added.length && !removed.length && !linksAdded && !linksRemoved && !sourcesAdded.length && !sourcesRemoved.length && !moved.length,
  };
}

/**
 * The decisions moved with their links: each decision on a content link goes to the number that link has in
 * the new content (the same two elements and kind of link). One whose link is gone stays under its number only
 * when no other link has taken it; else it goes, so it cannot apply to another link. People's own links keep theirs.
 */
export function remapDecisions(before: Obj, after: Obj, state: StrategyState): StrategyChange[] {
  const relsWas = relationsOf(before);
  const relsNow = relationsOf(after);
  const idByPair = new Map([...relsNow].map(([id, r]) => [linkText(r), id]));
  const customLinks = state.links ?? {};
  const next: Record<string, unknown> = {};
  const decisions = state.decisions ?? {};
  // People's own links and decisions on numbers the old content did not have: as they are.
  for (const [id, v] of Object.entries(decisions)) if (id in customLinks || !relsWas.has(id)) next[id] = v;
  for (const [id, v] of Object.entries(decisions)) {
    if (id in customLinks || !relsWas.has(id)) continue;
    const to = idByPair.get(linkText(relsWas.get(id)));
    if (to) next[to] = v;
    else if (!relsNow.has(id) && !(id in next)) next[id] = v;
  }
  return strategyChanges({ ...state, decisions }, { ...state, decisions: next }, ['decisions']);
}
